import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { HID } from "@/constants/cosecIds";

const PERSONAS = [
  {
    tid: HID.personaCoSec,
    href: "/company-secretaries",
    title: "Company Secretarial Software",
    sub: "Company Secretaries",
    img: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    body: "Manage statutory registers, automate filings, and oversee board activities efficiently.",
    bullets: ["Maintain statutory records", "Track shareholder information", "Manage board meetings and resolutions"],
  },
  {
    tid: HID.personaAccountants,
    href: "/accountants",
    title: "Accountants",
    sub: "Multi-entity accounting practices",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    body: "CoSec App helps accountants manage multiple entities, maintain accurate records, and stay on top of compliance requirements with ease.",
    bullets: ["Centralised client entities", "Filing deadline visibility", "Connected ID verification with CoVerify"],
  },
  {
    tid: HID.personaLegal,
    href: "/corporate-law",
    title: "Corporate Law Practitioners",
    sub: "Legal & corporate teams",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    body: "CoSec App supports legal professionals in managing corporate structures, maintaining compliance, and handling governance requirements efficiently.",
    bullets: ["Group structure visibility", "Beneficial ownership records", "Matter-ready audit trails"],
  },
];

export default function Personas() {
  return (
    <section id="personas" className="py-20 md:py-28 bg-secondary/40 border-y border-border">
      <div className="container-cv">
        <div className="max-w-3xl">
          <span className="section-eyebrow">Complete entity management platform</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.1]">
            Built for the teams that{" "}
            <span className="text-accent">keep businesses compliant.</span>
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PERSONAS.map((p) => (
            <article
              key={p.title}
              data-testid={p.tid}
              className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(14,58,95,0.25)]"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/75 via-primary/15 to-transparent" />
                <div className="absolute top-3 left-3 h-1 w-12 bg-accent rounded-full" />
                <div className="absolute bottom-3 left-4 right-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent/90">{p.sub}</div>
                  <h3 className="font-display text-lg md:text-xl font-bold text-white tracking-tight leading-tight">
                    {p.title}
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                <ul className="mt-4 space-y-2">
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-primary">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                      <span className="font-medium">{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={p.href || "/contact"}
                  className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-primary group-hover:text-accent transition-colors"
                >
                  Explore More <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
